import { CONVEYANCE_TYPES, type Location } from '@koala/sdk';
import { listLocations, getDeliveryCoverage } from '@koala/sdk/v4';
import orderBy from 'lodash/orderBy';
import { call, put, select, spawn, takeLatest, type SagaReturnType } from 'redux-saga/effects';
import basketActions from '../basket/actions';
import conveyanceModeActions from '../conveyanceMode/actions';
import globalActions from '../global/actions';
import { locationsActions as actions, locationActionTypes as actionTypes } from './actions';
import { locationsSliceSelector } from './reducer';
import { genericEventHandler } from '@/analytics/events';
import { GlobalEvents } from '@/analytics/events/constants';
import { ERROR_MESSAGES } from '@/constants/events';
import { COVERAGE_ERROR_COPY, isCoverageCheckFailure } from '@/constants/coverageErrorCopy';
import { createHttpClient } from '@/services/client';
import { fetchLocation } from '@/services/locations.service';
import { getOrigin } from '@/utils';
import { prepareErrorMessage } from '@/utils/global';
import * as ErrorReporter from '@/utils/errorReporter';
import { isValidZip, pickDefaultLocationId } from '@/utils/locations';
import { buildFetchFailureTags, shouldProbeSameOrigin } from '@/utils/fetchFailureTelemetry';
import { isNetworkError, isStatusError } from '@/utils/networkErrors';
import { probeSameOrigin, type SameOriginProbeResult } from '@/utils/sameOriginProbe';
import { TRANSPORT_RETRY_ATTEMPTS, retryWithBackoff } from '@/utils/requests';
import { type RootState } from '@/types/app';
import { type ILocationsState } from '@/types/locations';

/**
 * The store list is the first thing a diner waits on, so a dropped connection gets the longer
 * transport budget (~6 s) instead of failing to the connection-error state in ~1 s; a server
 * error keeps the default 3 attempts. (TEAM-15682)
 */
const FIRST_LOAD_RETRY_OPTIONS = { transportAttempts: TRANSPORT_RETRY_ATTEMPTS };

/** The coverage search answers 404 when the check completed and no location delivers there. */
const NOT_FOUND_STATUSES: readonly number[] = [404];

/**
 * Default the active location for a freshly loaded list to the nearest open one (TEAM-13911),
 * unless the diner already picked a location that is still listed: a later search or fetch
 * completing must not overwrite their choice. If their pick dropped out, the default applies.
 */
function* applyDefaultActiveLocation(list: Location[], conveyanceType?: string | null) {
  const { activeLocationId, activeLocationChosenByDiner }: ILocationsState =
    yield select(locationsSliceSelector);

  if (activeLocationChosenByDiner && list.some((location) => location.id === activeLocationId)) {
    return;
  }

  yield put(actions.setDefaultActiveLocation(pickDefaultLocationId(list, conveyanceType)));
}

/** The `extra` payload each /stores failure capture sends; unchanged by TEAM-15511. */
interface LocationFetchFailureExtra {
  eventName: string;
  errorResponse: unknown;
}

/**
 * Report a failed /stores location request to Sentry, tagged with why it failed (TEAM-15511).
 *
 * An opaque transport failure first asks the storefront's own origin whether it answers
 * (`utils/sameOriginProbe.ts`), which splits it into `api_unreachable` (only coo-api is
 * unreachable) and `offline` (neither is). The callers `spawn` this, so:
 * - the diner-facing failure state never waits for the probe;
 * - a new request (`takeLatest` cancels the old task, e.g. the diner pressing Retry) cannot drop
 *   the event — a spawned task is detached from its parent's cancellation.
 * When no probe is needed there is no async step, so the capture runs synchronously inside the
 * `spawn`, exactly when it did before. The tags are built after the probe settles, so a diner who
 * left the page meanwhile still reads as `page_unload`.
 */
export function* reportLocationFetchFailure(error: unknown, extra: LocationFetchFailureExtra) {
  let probe: SameOriginProbeResult = 'skipped';
  if (shouldProbeSameOrigin(error)) {
    try {
      const result: SagaReturnType<typeof probeSameOrigin> = yield call(probeSameOrigin);
      probe = result;
    } catch {
      // probeSameOrigin never throws; if it ever does, `probe` stays 'skipped'.
    }
  }

  ErrorReporter.captureException(error, 'error', extra, buildFetchFailureTags(error, { probe }));
}

export function* fetchAllLocationsSaga(action: ReturnType<typeof actions.fetchAllLocations>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const queryArgs = {
      paginate: false,
    };

    const response: SagaReturnType<typeof listLocations> = yield* retryWithBackoff(
      listLocations,
      FIRST_LOAD_RETRY_OPTIONS,
      action.magicBox ? { ...action.magicBox, ...queryArgs } : queryArgs,
      { client },
    );

    // Success
    const locations = action.results ? action.results.concat(response.data) : response.data;
    yield put(actions.fetchAllLocationsSuccess(locations));

    // Default the active location (no distances here, so only a single-location brand gets one).
    yield* applyDefaultActiveLocation(locations);
  } catch (error) {
    yield put(actions.viewAllLocationsFail());

    // Error event — matches fetchLocationsSaga / fetchMoreLocationsSaga reporting. The tags record
    // why the request failed (cause, status, page state, same-origin probe) without changing the
    // event (TEAM-15511).
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      "We couldn't load all restaurant locations.",
      error,
    );
    yield spawn(reportLocationFetchFailure, error, {
      eventName: ERROR_MESSAGES.FETCH_ALL_LOCATIONS_FAILURE,
      errorResponse,
    });
  }
}

export function* fetchLocationsSaga(action: ReturnType<typeof actions.fetchLocations>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    /** @TODO ensure that `magicBox` is defined. */
    // @ts-expect-error
    if (action.magicBox.page > 1) {
      /** @TODO ensure that `magicBox` is defined. */
      // @ts-expect-error
      yield put(actions.fetchMoreLocations(action.magicBox));

      // Stop locations loading
      yield put(actions.searchLocationsFail());
      return;
    }

    const response: SagaReturnType<typeof listLocations> = yield* retryWithBackoff(
      listLocations,
      FIRST_LOAD_RETRY_OPTIONS,
      /** @TODO ensure that `magicBox` is defined. */
      // @ts-expect-error
      action.magicBox,
      { client },
    );

    // Success
    yield put(actions.fetchLocationsSuccess(response.data, response.meta, action.params));

    // Default the active location (no distances here, so only a single-location brand gets one).
    yield* applyDefaultActiveLocation(response.data);
  } catch (error) {
    yield put(actions.searchLocationsFail());
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      "We couldn't load restaurant locations. Please refresh the page and try again.",
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error event, tagged with the failure cause (TEAM-15511)
    yield spawn(reportLocationFetchFailure, error, {
      eventName: ERROR_MESSAGES.FETCH_LOCATIONS_FAILURE,
      errorResponse,
    });
  }
}

export function* fetchMoreLocationsSaga(action: ReturnType<typeof actions.fetchMoreLocations>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof listLocations> = yield call(
      /** @TODO ensure that `magicBox` is defined. */
      // @ts-expect-error
      listLocations,
      action.magicBox,
      { client },
    );

    // Success
    yield put(actions.fetchMoreLocationsSuccess(response.data, response.meta, action.params));
  } catch (error) {
    yield put(actions.searchLocationsFail());
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      "We couldn't load more restaurant locations. Please refresh the page and try again.",
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error event, tagged with the failure cause (TEAM-15511)
    yield spawn(reportLocationFetchFailure, error, {
      eventName: ERROR_MESSAGES.FETCH_MORE_LOCATIONS_FAILURE,
      errorResponse,
    });
  }
}

export function* searchLocationsSaga(action: ReturnType<typeof actions.searchLocations>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    let search: Record<string, unknown> = {};

    if (action.params?.address) {
      search = {
        address: action.params.address,
      };
    } else if (action.params?.latitude && action.params?.longitude) {
      search = {
        latitude: action.params.latitude,
        longitude: action.params.longitude,
      };
    } else {
    }

    const response: SagaReturnType<typeof listLocations> = yield* retryWithBackoff(
      listLocations,
      FIRST_LOAD_RETRY_OPTIONS,
      {
        ...action.magicBox,
        search,
        distance: action?.params?.distance ?? 10, // default to 10 miles
      },
      { client },
    );

    const sortedLocations = orderBy(response.data, 'distance_miles', 'asc');

    // If we have results or we've already performed a no results search
    if (response.data.length || action.previousAction) {
      // Success
      yield put(actions.searchLocationsSuccess(sortedLocations, response.meta, action.params));

      // Default from the distance-sorted list, not the unsorted response (TEAM-13911).
      yield* applyDefaultActiveLocation(sortedLocations, action.conveyanceType);

      // KA event
      // NOTE: it only handles US zip codes.
      const inputtedAddress = action.params?.address ?? '';
      const isZip = isValidZip(inputtedAddress);
      const details = isZip
        ? `zip_code; ${sortedLocations.length}`
        : sortedLocations?.length?.toString();

      genericEventHandler(GlobalEvents.LOCATIONS__SEARCH_LOCATIONS, {
        name: action.conveyanceType,
        details,
      });
    } else {
      // If we have no results and have no already re-searched with an expanded radius
      const expandedParams = Object.assign({}, action.params, {
        distance: 25,
      });

      yield put(
        /** @TODO ensure that `magicBox` is defined. */
        // @ts-expect-error
        actions.searchLocations(action.magicBox, expandedParams, action.type),
      );
    }
  } catch (error) {
    if ((error as Response).status === 404) {
      const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
        prepareErrorMessage,
        null,
        error,
      );

      // The geocoder could not resolve what the diner typed. Rather than a dismissable
      // toast that leaves them nothing to act on (TEAM-14316), surface a recoverable
      // empty state: their typed text is preserved to correct and a "view all locations"
      // route is offered inline. Unlike a zero-results search there is no point retrying
      // with a wider radius — the same string still will not geocode.
      yield put(actions.searchLocationsUnresolved(action.params));

      // Keep reporting the miss so the diner-blocking-signals dashboard (COO-WEBAPP-3S5)
      // still tracks volume; the diner now recovers in-page instead of abandoning.
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.INVALID_ZIP_ERROR,
        errorResponse,
      });

      return;
    }

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      "We couldn't complete your search. Please try again.",
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message));

    // Error event, tagged with the failure cause (TEAM-15511)
    yield spawn(reportLocationFetchFailure, error, {
      eventName: ERROR_MESSAGES.SEARCH_LOCATION_ERROR,
      errorResponse,
    });

    yield put(actions.searchLocationsFail());
  }
}

export function* fetchLocationSaga(action: ReturnType<typeof actions.fetchLocation>) {
  const { id, lastUpdatedAt, suppressErrorToast } = action;

  // A diner can enter a store route before the location id has resolved (deep link,
  // store-picker before hydration, or a null-geoip session). Dispatching the fetch
  // with an undefined id issues a request to `/v1/ordering/store-locations/undefined`,
  // which coo-api answers 404 — the highest-volume COO error (TEAM-14044 / COO-WEBAPP-46S).
  // Defer silently: the store-picker / hydration flow re-dispatches once the id resolves.
  if (id == null) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const response: SagaReturnType<typeof fetchLocation> = yield* retryWithBackoff(
      fetchLocation,
      {},
      { id, lastUpdatedAt },
      { client },
    );

    // Success
    yield put(actions.fetchLocationSuccess(response.data));
  } catch (error) {
    // @ts-expect-error: error is unknown
    if (error?.status === 304) {
      yield put(actions.fetchLocationNotModified());
    } else {
      yield put(actions.searchLocationsFail());

      // On the order-confirmation page the store location is secondary to an order the diner
      // has already paid for, so it opts out of the blocking, focus-trapping banner that tells
      // them to refresh (TEAM-14521). Every other route keeps the notification.
      if (!suppressErrorToast) {
        if (isNetworkError(error)) {
          // A dropped connection gets the shared "can't connect" copy and a real retry of this
          // same fetch, instead of "Please refresh the page" with no way to act on it
          // (TEAM-14780 QA). A real status-code failure keeps the message below.
          yield put(globalActions.displayConnectionErrorToast(action));
        } else {
          const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
            prepareErrorMessage,
            "We couldn't load this restaurant location. Please refresh the page and try again.",
          );
          yield put(globalActions.displayErrorToast(errorResponse.message, true));
        }
      }

      // Capture with per-partner attribution and force this signature into its own Sentry
      // issue (TEAM-14521 AC5-AC7). This runs on every route, not just confirmation.
      const organizationId = (yield select(
        (state: RootState) => state.app.organization.organization?.id,
      )) as number | null | undefined;
      const err = error as { status?: number; json?: { error?: string }; url?: string } | null;

      ErrorReporter.reportStoreLocationFetchFailure(error, {
        locationId: id,
        organizationId,
        status: err?.status,
        urlHint: err?.url,
        errorSlug: err?.json?.error ?? null,
      });
    }
  }
}

function* hydrateLocationSaga(action: ReturnType<typeof actions.hydrateLocation>) {
  yield put({ type: actionTypes.LOCATION_SUCCESS, detail: action.location });
}

export function* refreshBasketLocationSaga(
  action: ReturnType<typeof actions.refreshBasketLocation>,
) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    // TEAM-14725: retry a transient network hiccup (Load failed / Failed to fetch, or a
    // retryable status) under the same policy as the sibling fetchLocationSaga, instead
    // of the previous bare call() that failed on the first attempt and only console.log'd.
    const response: SagaReturnType<typeof fetchLocation> = yield* retryWithBackoff(
      fetchLocation,
      {},
      /** @TODO ensure that `id` is defined. */
      // @ts-expect-error
      { id: action.id },
      { client },
    );

    // Success
    yield put(basketActions.rehydrateLocalStorageLocation(response.data));
  } catch (error) {
    console.log('Rehydrating local storage location failed');
  }
}

export function* searchDeliveryCoverageSaga(
  action: ReturnType<typeof actions.searchDeliveryCoverage>,
) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof getDeliveryCoverage> = yield call(
      /** @TODO ensure that `address` is defined. */
      // @ts-expect-error
      getDeliveryCoverage,
      action.address,
      { client },
    );
    const location = response.data;
    if (location) {
      // Set delivery address on redux and then redirect to the store page.
      yield put(
        conveyanceModeActions.setDeliveryAddress(
          /** @TODO ensure that `address` is defined. */
          // @ts-expect-error
          action.address,
          location,
          false,
          true,
        ),
      );

      // Success
      yield put(actions.searchDeliveryCoverageSuccess());

      // KA event
      genericEventHandler(GlobalEvents.LOCATIONS__SEARCH_LOCATIONS, {
        name: CONVEYANCE_TYPES.DELIVERY,
      });
    }
  } catch (error) {
    // Failure
    yield put(actions.searchDeliveryCoverageFail());

    // Neither Sentry message below names the diner's address. It is PII, and the backend half of
    // TEAM-14744 removed it from coo-api's coverage logs for the same reason — see coo-api
    // `docs/LOGGING_SENSITIVE_DATA.md`. The status and event name already say which branch fired.
    if (isStatusError(error, NOT_FOUND_STATUSES)) {
      // Error Message
      yield put(actions.searchDeliveryCoverageNoResults());

      // KA event
      genericEventHandler(GlobalEvents.LOCATIONS__LOCATION_SEARCH_NOT_FOUND, {
        name: CONVEYANCE_TYPES.DELIVERY,
      });
    } else if (isCoverageCheckFailure(error)) {
      // coo-api could not complete the search (HTTP 502). Deliberately does NOT
      // dispatch searchDeliveryCoverageNoResults: that sets
      // `noDeliveryLocationsFound`, which asserts no restaurant delivers here —
      // a claim this response does not support (AC #11). Without this branch the
      // failure fell through to the `else` below, which reports to Sentry and
      // tells the diner nothing at all, because SEARCH_DELIVERY_COVERAGE_FAILURE
      // sets `error: false`.
      yield put(globalActions.displayErrorToast(COVERAGE_ERROR_COPY.checkFailed, true));

      const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
        prepareErrorMessage,
        'Delivery coverage search could not complete.',
        error,
      );
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.DELIVERY_COVERAGE_CHECK_FAILED,
        errorResponse,
      });
    } else {
      // Error Notification
      const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
        prepareErrorMessage,
        // No location name: `searchDeliveryCoverage` builds `{ type, address }` only
        // (`actions.ts:107`), so `action.location` is structurally always undefined here and
        // the interpolation this replaced rendered the literal "undefined".
        'Failed to search delivery coverage.',
        error,
      );
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.SEARCH_DELIVERY_COVERAGE_ERROR,
        errorResponse,
      });
    }
  }
}

export default function* rootSaga() {
  yield takeLatest(actionTypes.FETCH_LOCATIONS, fetchLocationsSaga);
  yield takeLatest(actionTypes.FETCH_ALL_LOCATIONS, fetchAllLocationsSaga);
  yield takeLatest(actionTypes.FETCH_LOCATION, fetchLocationSaga);
  yield takeLatest(actionTypes.FETCH_LOCATION_IF_MODIFIED, fetchLocationSaga);
  yield takeLatest(actionTypes.HYDRATE_LOCATION, hydrateLocationSaga);
  yield takeLatest(actionTypes.SEARCH_LOCATIONS, searchLocationsSaga);
  yield takeLatest(actionTypes.FETCH_MORE_LOCATIONS, fetchMoreLocationsSaga);
  yield takeLatest(actionTypes.REFRESH_BASKET_LOCATION, refreshBasketLocationSaga);
  yield takeLatest(actionTypes.SEARCH_DELIVERY_COVERAGE, searchDeliveryCoverageSaga);
}
